import { useId } from 'react';

/**
 * SculpturalMesh — deterministic hairline "spider-web" wireframe of a
 * twisted saddle surface, projected in 3D. Pure SVG, no animation.
 * Stroke runs blue → Plasmics cyan → Plasmics orange; the fill is always none,
 * so the page's white stays white.
 */

type Variant = 'saddle' | 'funnel' | 'wave';

interface MeshOptions {
  rings: number;
  spokes: number;
  twist: number;
  tiltX: number;
  tiltZ: number;
  variant: Variant;
}

const SIZE = 1000;
const cache = new Map<string, { rings: string; spokes: string }>();

function buildMesh(o: MeshOptions) {
  const key = JSON.stringify(o);
  const hit = cache.get(key);
  if (hit) return hit;

  const cx = Math.cos(o.tiltX), sx = Math.sin(o.tiltX);
  const cz = Math.cos(o.tiltZ), sz = Math.sin(o.tiltZ);
  const cam = 3.2;

  const point = (r: number, t: number): [number, number] => {
    const th = t + o.twist * r * r;
    let x = r * Math.cos(th);
    let y = r * Math.sin(th);
    let z: number;
    if (o.variant === 'saddle') z = 0.55 * r * r * Math.cos(2 * t) + 0.06 * Math.sin(5 * t) * r;
    else if (o.variant === 'funnel') z = -0.9 * Math.pow(1 - r, 2.2) + 0.12 * r * Math.sin(3 * t);
    else z = 0.28 * Math.sin(3 * r * Math.PI) * (1 - 0.4 * r) + 0.18 * r * Math.cos(3 * t);
    // rotate around Z then X
    const x1 = x * cz - y * sz;
    const y1 = x * sz + y * cz;
    const y2 = y1 * cx - z * sx;
    const z2 = y1 * sx + z * cx;
    const p = cam / (cam - z2);
    x = x1 * p;
    y = y2 * p;
    return [SIZE / 2 + x * SIZE * 0.42, SIZE / 2 + y * SIZE * 0.42];
  };

  const f = (n: number) => n.toFixed(1);
  const steps = 96;
  let rings = '';
  for (let i = 1; i <= o.rings; i++) {
    const r = Math.pow(i / o.rings, 1.15);
    for (let s = 0; s <= steps; s++) {
      const [px, py] = point(r, (s / steps) * Math.PI * 2);
      rings += `${s === 0 ? 'M' : 'L'}${f(px)} ${f(py)}`;
    }
  }
  let spokes = '';
  const rs = 28;
  for (let k = 0; k < o.spokes; k++) {
    const t = (k / o.spokes) * Math.PI * 2;
    for (let s = 0; s <= rs; s++) {
      const [px, py] = point(0.02 + (s / rs) * 0.98, t);
      spokes += `${s === 0 ? 'M' : 'L'}${f(px)} ${f(py)}`;
    }
  }
  const out = { rings, spokes };
  cache.set(key, out);
  return out;
}

export function SculpturalMesh({
  className = '',
  rings = 26,
  spokes = 84,
  twist = 0.9,
  tiltX = 1.05,
  tiltZ = 0.35,
  variant = 'saddle',
  opacity = 1,
  gradient = [0.2, 0.1, 0.75, 0.85],
}: Partial<MeshOptions> & { className?: string; opacity?: number; gradient?: [number, number, number, number] }) {
  const { rings: ringPath, spokes: spokePath } = buildMesh({ rings, spokes, twist, tiltX, tiltZ, variant });
  const id = `pm-mesh-${useId().replace(/:/g, '')}`;
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      preserveAspectRatio="xMidYMid meet"
      className={`plasmics-mesh ${className}`}
      style={{ opacity }}
    >
      <defs>
        <linearGradient id={id} x1={SIZE * gradient[0]} y1={SIZE * gradient[1]} x2={SIZE * gradient[2]} y2={SIZE * gradient[3]} gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="hsl(218 88% 52%)" />
          <stop offset="0.5" stopColor="hsl(190 100% 42%)" />
          <stop offset="1" stopColor="hsl(22 98% 62%)" />
        </linearGradient>
      </defs>
      <g fill="none" stroke={`url(#${id})`} strokeLinejoin="round" strokeLinecap="round">
        <path d={spokePath} strokeWidth="0.6" strokeOpacity="0.6" vectorEffect="non-scaling-stroke" />
        <path d={ringPath} strokeWidth="0.65" strokeOpacity="0.85" vectorEffect="non-scaling-stroke" />
      </g>
    </svg>
  );
}
